import { useEffect, useState } from 'react';
import type { BarcodeOwner } from '@/infrastructure/ProductAttributesClient';
import { skinSeoulProductsClient } from '@/infrastructure/SkinSeoulProductsClient';
import { StockHistoryLink } from './StockHistoryLink';

export interface BarcodeOwnerCardProps {
  barcode: string;
  owner: BarcodeOwner;
}

interface OwnerProduct {
  sku: string;
  name: string;
  image?: string | undefined;
}

/**
 * Why a barcode was not saved: another product already carries it (SSD-7532). The refusal is correct, so the
 * card does not offer to take the code away from that product; it shows the product, so the desk can tell at a
 * glance whether the item in hand is that product or whether it scanned the wrong barcode.
 */
export function BarcodeOwnerCard({ barcode, owner }: BarcodeOwnerCardProps) {
  // undefined while the product is read, null when it could not be read (a draft, or the lookup failed).
  const [product, setProduct] = useState<OwnerProduct | null | undefined>(undefined);

  useEffect(() => {
    let current = true;
    setProduct(undefined);
    skinSeoulProductsClient
      .getProductById(String(owner.productId))
      .then((found) => {
        if (current) {
          setProduct({ sku: found.sku, name: found.name, image: found.productImageUrls?.[0] });
        }
      })
      .catch(() => {
        if (current) {
          setProduct(null);
        }
      });
    return () => {
      current = false;
    };
  }, [owner.productId]);

  const name = product?.name ?? owner.name ?? `Product #${owner.productId}`;

  return (
    <div
      className="barcode-owner"
      role="alert"
      aria-label="Barcode already on another product"
      // The longer guidance stays a hover away: the card sits in the narrow Barcode column and has to stay short.
      title={`Not saved: ${barcode} is already on this product. If the item in your hand is it, it belongs to another order; if not, scan the barcode printed on the item.`}
    >
      {/* The code is still in the box above, so the card names only where it already belongs. */}
      <div className="barcode-owner-head">Not saved: already on</div>
      {product?.image ? <img src={product.image} alt="" /> : <span className="barcode-owner-noimg">IMG</span>}
      <div className="barcode-owner-name" title={name}>
        {name}
      </div>
      {product?.sku?.trim() && (
        <div className="barcode-owner-sku num">
          SKU <StockHistoryLink sku={product.sku} />
        </div>
      )}
    </div>
  );
}
